import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listTemplates } from '@/lib/broadcast.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import type { Template } from '@/types/broadcast.ts';

export interface TemplatesQuery {
  /** The page the Templates table is showing. */
  templates: Template[];
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  total: number;
  isPending: boolean;
  isError: boolean;
}

/**
 * The template list, held once for the whole page.
 *
 * Send and Templates are two views of the same set, so they read one query
 * rather than fetching the list twice and disagreeing after a save.
 */
export function useBroadcastTemplates(enabled: boolean): TemplatesQuery {
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['templates', page],
    queryFn: () => listTemplates(page, PAGE_SIZE),
    /* Nothing to show on the History tab, so nothing is asked for there. */
    enabled,
  });

  const templates = query.data?.data ?? [];

  return {
    templates,
    page,
    setPage,
    pageSize: PAGE_SIZE,
    total: query.data?.meta.totalItems ?? 0,
    isPending: query.isPending,
    isError: query.isError,
  };
}
